<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import TalkviaInventoryProductsAPI from 'dashboard/api/talkviaInventoryProducts';
import TalkviaInventorySuppliersAPI from 'dashboard/api/talkviaInventorySuppliers';
import TalkviaInventoryMovementsAPI from 'dashboard/api/talkviaInventoryMovements';
import TalkviaInventoryProductGroupsAPI from 'dashboard/api/talkviaInventoryProductGroups';

const copy = {
  title: 'Estoque',
  subtitle:
    'Cadastre produtos, fornecedores e acompanhe entradas, saídas e alertas de estoque baixo.',
  actions: {
    filter: 'Filtrar',
    clear: 'Limpar',
    newProduct: 'Novo produto',
    newSupplier: 'Novo fornecedor',
    movement: 'Movimentar',
    saveProduct: 'Salvar produto',
    saveSupplier: 'Salvar fornecedor',
    saveMovement: 'Salvar movimentação',
    saveGroup: 'Salvar grupo',
    createGroup: 'Criar grupo',
    cancel: 'Cancelar',
    edit: 'Editar',
    delete: 'Excluir',
    addProduct: 'Cadastrar primeiro produto',
  },
  cards: {
    total: 'Produtos',
    active: 'Ativos',
    lowStock: 'Estoque baixo',
    stockValue: 'Valor em estoque',
  },
  fields: {
    brand: 'Talkvia',
    search: 'Buscar produto',
    searchPlaceholder: 'Nome, SKU ou código',
    supplier: 'Fornecedor',
    category: 'Categoria',
    status: 'Status',
    actions: 'Ações',
    name: 'Nome',
    sku: 'SKU',
    barcode: 'Código de barras',
    unit: 'Unidade',
    costPrice: 'Custo',
    salePrice: 'Venda',
    quantity: 'Quantidade',
    lowStock: 'Estoque mínimo',
    location: 'Local de estoque',
    newLocation: 'Novo local',
    description: 'Descrição',
    returnable: 'Retorna ao estoque',
    document: 'Documento',
    email: 'E-mail',
    phone: 'Telefone',
    contactName: 'Contato',
    website: 'Site',
    notes: 'Observações',
    movementType: 'Tipo',
    reference: 'Referência',
    unitCost: 'Custo unitário',
    composition: 'Composição',
    available: 'Disponível',
    groupName: 'Nome do grupo',
    groupSalePrice: 'Preço de venda',
    groupComposition: 'Composição do grupo',
    product: 'Produto',
    shortQuantity: 'Qtde',
  },
  statuses: {
    all: 'Todos',
    allCategories: 'Todas',
    active: 'Ativos',
    inactive: 'Inativos',
  },
  movementTypes: {
    inbound: 'Entrada',
    outbound: 'Saída',
    adjustment: 'Ajuste',
  },
  sections: {
    products: 'Produtos cadastrados',
    groups: 'Grupos de produtos',
    suppliers: 'Fornecedores',
    alerts: 'Alertas',
    movements: 'Últimas movimentações',
  },
  empty: {
    products: 'Nenhum produto encontrado.',
    groups: 'Nenhum grupo cadastrado.',
    suppliers: 'Nenhum fornecedor cadastrado.',
    alerts: 'Nenhum produto abaixo do estoque mínimo.',
  },
  groupActions: {
    newGroup: 'Novo grupo',
    editGroup: 'Editar grupo',
    addItem: 'Adicionar produto',
  },
  labels: {
    loading: 'Carregando...',
    editProduct: 'Editar produto',
    editSupplier: 'Editar fornecedor',
    noSupplier: 'Sem fornecedor',
    selectProduct: 'Selecione um produto',
    selectUnit: 'Selecione a unidade',
    selectLocation: 'Selecione o local',
    addLocation: 'Cadastrar local',
    inStock: 'em estoque.',
    minimum: 'Mínimo:',
    period: '.',
    separator: '·',
    inventoryInfo: 'Informações do estoque',
    required: 'Obrigatório',
    optional: 'Opcional',
    productIdentification: 'Identificação',
    productCommercial: 'Valores comerciais',
    productInventory: 'Controle de estoque',
    productDetails: 'Detalhes adicionais',
    supplierContact: 'Contato e identificação',
    supplierDetails: 'Detalhes do fornecedor',
    movementBalance: 'Prévia do saldo',
    currentBalance: 'Saldo atual',
    projectedBalance: 'Saldo após movimentação',
    directQuantityWarning:
      'Em produtos existentes, altere o saldo por uma movimentação para manter o histórico.',
    addNewLocation: 'Cadastrar novo local',
    hideNewLocation: 'Cancelar novo local',
    noMovements: 'Nenhuma movimentação registrada.',
    productActive: 'Produto ativo',
    supplierActive: 'Fornecedor ativo',
    returnableHint:
      'Quando usado em contratos, o item poderá retornar ao estoque ao final da operação.',
    productEmptyHint: 'Cadastre um produto ou ajuste os filtros para começar.',
    groupNamePlaceholder: 'Ex: Jogo de Mesa e Cadeiras',
    optionalDescription: 'Descrição opcional',
    groupEmptyHint: 'Clique em “Adicionar produto” para montar o grupo.',
    groupDescription:
      'Defina os produtos que compõem o grupo e o valor de venda combinado.',
    productDescription:
      'Organize identificação, valores e regras de estoque do produto.',
    supplierDescription:
      'Centralize os dados usados em compras, reposições e contatos comerciais.',
    estimatedMargin: 'Margem estimada',
    productActiveHint: 'Disponível para uso e movimentações.',
    supplierActiveHint: 'Disponível para associação a produtos e entradas.',
    productDescriptionPlaceholder:
      'Características, composição ou observações internas',
    supplierNotesPlaceholder:
      'Prazos, condições comerciais e observações internas',
    movementDescription:
      'Registre entradas, saídas ou ajuste o saldo físico mantendo o histórico.',
    movementData: 'Dados da movimentação',
    searchProduct: 'Buscar produto',
    noProductsRegistered: 'Nenhum produto cadastrado',
    adjustmentHint: 'No ajuste, este valor se torna o novo saldo.',
    movementReferencePlaceholder: 'Nota fiscal, pedido ou motivo',
    movementNotesPlaceholder: 'Detalhes que ajudem a auditar esta movimentação',
    quantityMultiplier: '×',
  },
  messages: {
    loadFailed: 'Não foi possível carregar o estoque.',
    saved: 'Registro salvo.',
    movementSaved: 'Movimentação salva.',
    deleted: 'Produto excluído.',
    saveFailed: 'Não foi possível salvar.',
    deleteFailed: 'Não foi possível excluir.',
    deleteTitle: 'Excluir produto',
    confirmDelete: productName =>
      `Excluir o produto "${productName}"? Esta ação não pode ser desfeita.`,
    deleteBlocked:
      'Este produto está vinculado a contratos ativos e não pode ser excluído.',
    managePermissionRequired:
      'Você não tem permissão para cadastrar produto, fornecedor ou movimentação.',
    productRequired: 'Informe o nome e a unidade do produto.',
    supplierRequired: 'Informe o nome do fornecedor.',
    movementRequired: 'Selecione um produto e informe uma quantidade válida.',
    negativeStock: 'A saída não pode deixar o estoque negativo.',
  },
};

const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const currentAccount = useMapGetter('getCurrentAccount');

const products = ref([]);
const suppliers = ref([]);
const movements = ref([]);
const summary = ref({});
const isLoading = ref(false);
const isSaving = ref(false);
const activeInventoryTab = ref('alerts');
const editingProductId = ref(null);
const editingSupplierId = ref(null);
const productDialogRef = ref(null);
const supplierDialogRef = ref(null);
const movementDialogRef = ref(null);
const deleteDialogRef = ref(null);
const productToDelete = ref(null);
const menuAnchor = ref(null);
const activeMenuProduct = ref(null);
const activeMenuGroup = ref(null);
const groupDialogRef = ref(null);
const groups = ref([]);
const editingGroupId = ref(null);
const stockLocations = ref([]);
const newStockLocation = ref('');
const showNewLocationField = ref(false);

const filters = reactive({
  q: '',
  supplier_id: '',
  category: '',
  active: '',
});

const productForm = reactive({
  name: '',
  sku: '',
  barcode: '',
  category: '',
  unit: 'un',
  cost_price: 0,
  sale_price: 0,
  quantity_on_hand: 0,
  low_stock_threshold: 0,
  location: '',
  description: '',
  talkvia_inventory_supplier_id: '',
  active: true,
  returnable: true,
});

const supplierForm = reactive({
  name: '',
  document: '',
  email: '',
  phone: '',
  contact_name: '',
  website: '',
  notes: '',
  active: true,
});

const groupForm = reactive({
  name: '',
  description: '',
  sale_price: 0,
  active: true,
  items: [],
});

const movementForm = reactive({
  talkvia_inventory_product_id: '',
  talkvia_inventory_supplier_id: '',
  movement_type: 'inbound',
  quantity: 1,
  unit_cost: '',
  reference: '',
  notes: '',
});

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const unitOptions = [
  { value: 'un', label: 'Unidade' },
  { value: 'cx', label: 'Caixa' },
  { value: 'pct', label: 'Pacote' },
  { value: 'kg', label: 'Quilograma' },
  { value: 'g', label: 'Grama' },
  { value: 'l', label: 'Litro' },
  { value: 'ml', label: 'Mililitro' },
  { value: 'm', label: 'Metro' },
  { value: 'm2', label: 'Metro quadrado' },
  { value: 'par', label: 'Par' },
  { value: 'kit', label: 'Kit' },
  { value: 'serv', label: 'Serviço' },
];

const supplierOptions = computed(() => [
  { value: '', label: copy.labels.noSupplier },
  ...suppliers.value.map(supplier => ({
    value: supplier.id,
    label: supplier.name,
  })),
]);

const movementTypeOptions = computed(() => [
  { value: 'inbound', label: copy.movementTypes.inbound },
  { value: 'outbound', label: copy.movementTypes.outbound },
  { value: 'adjustment', label: copy.movementTypes.adjustment },
]);

const formatMoney = value => moneyFormatter.format(Number(value || 0));
const formatQuantity = value => Number(value || 0).toLocaleString('pt-BR');
const parseMoney = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return Number(digits || 0) / 100;
};
const formatDateTime = value =>
  value ? new Date(value).toLocaleString('pt-BR') : '-';
const movementLabel = value => copy.movementTypes[value] || value || '-';
const filterInputClass =
  '!h-12 !rounded-lg !bg-n-solid-2 !px-4 !text-sm !text-n-slate-12 placeholder:!text-n-slate-10';

const categoryOptions = computed(() => [
  ...new Set(products.value.map(product => product.category).filter(Boolean)),
]);

const supplierFilterOptions = computed(() => [
  { label: copy.statuses.all, value: '' },
  ...suppliers.value.map(supplier => ({
    label: supplier.name,
    value: supplier.id,
  })),
]);

const categoryFilterOptions = computed(() => [
  { label: copy.statuses.allCategories, value: '' },
  ...categoryOptions.value.map(category => ({
    label: category,
    value: category,
  })),
]);

const statusFilterOptions = computed(() => [
  { label: copy.statuses.all, value: '' },
  { label: copy.statuses.active, value: 'true' },
  { label: copy.statuses.inactive, value: 'false' },
]);

const stockLocationStorageKey = computed(
  () => `talkvia-inventory-locations-${currentAccount.value?.id || 'default'}`
);

const locationOptions = computed(() => [
  ...new Set(
    [
      ...stockLocations.value,
      ...products.value.map(product => product.location).filter(Boolean),
    ]
      .map(location => String(location).trim())
      .filter(Boolean)
  ),
]);

const currencyFieldModel = field =>
  computed({
    get: () => formatMoney(productForm[field]),
    set: value => {
      productForm[field] = parseMoney(value);
    },
  });

const productCostDisplay = currencyFieldModel('cost_price');
const productSaleDisplay = currencyFieldModel('sale_price');
const groupSaleDisplay = computed({
  get: () => formatMoney(groupForm.sale_price),
  set: value => {
    groupForm.sale_price = parseMoney(value);
  },
});
const movementUnitCostDisplay = computed({
  get: () => formatMoney(movementForm.unit_cost),
  set: value => {
    movementForm.unit_cost = parseMoney(value);
  },
});

const lowStockProducts = computed(() =>
  products.value.filter(product => product.low_stock)
);

const cards = computed(() => [
  {
    label: copy.cards.total,
    value: summary.value.total_products || 0,
    icon: 'i-lucide-package',
  },
  {
    label: copy.cards.active,
    value: summary.value.active_products || 0,
    icon: 'i-lucide-check-circle',
  },
  {
    label: copy.cards.lowStock,
    value: summary.value.low_stock_products || 0,
    icon: 'i-lucide-triangle-alert',
  },
  {
    label: copy.cards.stockValue,
    value: formatMoney(summary.value.stock_value || 0),
    icon: 'i-lucide-banknote',
  },
]);

const inventoryTabs = computed(() => [
  {
    key: 'alerts',
    label: copy.sections.alerts,
    icon: 'i-lucide-triangle-alert',
  },
  {
    key: 'suppliers',
    label: copy.sections.suppliers,
    icon: 'i-lucide-truck',
  },
  {
    key: 'movements',
    label: copy.sections.movements,
    icon: 'i-lucide-repeat-2',
  },
]);

const inventoryTabBarItems = computed(() =>
  inventoryTabs.value.map(tab => ({ ...tab, label: tab.label }))
);

const activeInventoryTabIndex = computed(() =>
  Math.max(
    inventoryTabs.value.findIndex(tab => tab.key === activeInventoryTab.value),
    0
  )
);

const selectedMovementProduct = computed(() =>
  products.value.find(
    product => product.id === movementForm.talkvia_inventory_product_id
  )
);

const movementProjectedQuantity = computed(() => {
  const current = Number(selectedMovementProduct.value?.quantity_on_hand || 0);
  const quantity = Number(movementForm.quantity || 0);
  if (movementForm.movement_type === 'adjustment') return quantity;
  if (movementForm.movement_type === 'outbound') return current - quantity;
  return current + quantity;
});

const movementWouldBeNegative = computed(
  () => !!selectedMovementProduct.value && movementProjectedQuantity.value < 0
);

const productFormValid = computed(
  () => productForm.name.trim() && productForm.unit
);
const supplierFormValid = computed(() => supplierForm.name.trim());
const groupFormValid = computed(
  () =>
    groupForm.name.trim() &&
    groupForm.items.length > 0 &&
    groupForm.items.every(item => item.product_id && Number(item.quantity) > 0)
);
const movementFormValid = computed(
  () =>
    movementForm.talkvia_inventory_product_id &&
    Number(movementForm.quantity) > 0 &&
    !movementWouldBeNegative.value
);

const productMargin = computed(
  () =>
    Number(productForm.sale_price || 0) - Number(productForm.cost_price || 0)
);

const productMarginPercent = computed(() => {
  const cost = Number(productForm.cost_price || 0);
  return cost > 0 ? (productMargin.value / cost) * 100 : 0;
});

const activeFilterCount = computed(
  () =>
    [filters.q, filters.supplier_id, filters.category, filters.active].filter(
      value => value !== ''
    ).length
);

const canManageInventory = computed(() => {
  if (currentUser.value?.type === 'SuperAdmin') return true;
  if (currentRole.value === 'administrator') return true;

  return (currentAccount.value?.permissions || []).includes('inventory_manage');
});

const productName = product =>
  [product.name, product.sku ? `#${product.sku}` : '']
    .filter(Boolean)
    .join(' ');

const locationSelectOptions = computed(() =>
  locationOptions.value.map(location => ({ value: location, label: location }))
);

const productSelectOptions = computed(() =>
  products.value.map(product => ({
    value: product.id,
    label: productName(product),
  }))
);

const requestParams = () => ({
  q: filters.q || undefined,
  supplier_id: filters.supplier_id || undefined,
  category: filters.category || undefined,
  active: filters.active === '' ? undefined : filters.active,
});

const loadInventory = async () => {
  isLoading.value = true;
  try {
    const [
      productsResponse,
      suppliersResponse,
      movementsResponse,
      groupsResponse,
    ] = await Promise.all([
      TalkviaInventoryProductsAPI.get(requestParams()),
      TalkviaInventorySuppliersAPI.get(),
      TalkviaInventoryMovementsAPI.get(),
      TalkviaInventoryProductGroupsAPI.get(),
    ]);

    products.value = productsResponse.data.products || [];
    summary.value = productsResponse.data.summary || {};
    suppliers.value = suppliersResponse.data || [];
    movements.value = movementsResponse.data || [];
    groups.value = groupsResponse.data || [];
  } catch {
    useAlert(copy.messages.loadFailed);
  } finally {
    isLoading.value = false;
  }
};

const loadStockLocations = () => {
  try {
    stockLocations.value = JSON.parse(
      localStorage.getItem(stockLocationStorageKey.value) || '[]'
    );
  } catch {
    stockLocations.value = [];
  }
};

const persistStockLocations = () => {
  localStorage.setItem(
    stockLocationStorageKey.value,
    JSON.stringify(stockLocations.value)
  );
};

const addStockLocation = () => {
  const location = newStockLocation.value.trim();
  if (!location) return;

  stockLocations.value = [
    location,
    ...stockLocations.value.filter(item => item !== location),
  ];
  productForm.location = location;
  newStockLocation.value = '';
  showNewLocationField.value = false;
  persistStockLocations();
};

const resetProductForm = () => {
  editingProductId.value = null;
  newStockLocation.value = '';
  showNewLocationField.value = false;
  Object.assign(productForm, {
    name: '',
    sku: '',
    barcode: '',
    category: '',
    unit: 'un',
    cost_price: 0,
    sale_price: 0,
    quantity_on_hand: 0,
    low_stock_threshold: 0,
    location: '',
    description: '',
    talkvia_inventory_supplier_id: '',
    active: true,
    returnable: true,
  });
};

const resetSupplierForm = () => {
  editingSupplierId.value = null;
  Object.assign(supplierForm, {
    name: '',
    document: '',
    email: '',
    phone: '',
    contact_name: '',
    website: '',
    notes: '',
    active: true,
  });
};

const resetMovementForm = product => {
  Object.assign(movementForm, {
    talkvia_inventory_product_id: product?.id || '',
    talkvia_inventory_supplier_id: product?.talkvia_inventory_supplier_id || '',
    movement_type: 'inbound',
    quantity: 1,
    unit_cost: product?.cost_price || '',
    reference: '',
    notes: '',
  });
};

const openProductDialog = (product = null) => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }

  resetProductForm();
  if (product) {
    editingProductId.value = product.id;
    Object.assign(productForm, {
      ...product,
      description: product.description || '',
      location: product.location || '',
      talkvia_inventory_supplier_id:
        product.talkvia_inventory_supplier_id || '',
    });
  }
  productDialogRef.value?.open();
};

const openSupplierDialog = (supplier = null) => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }

  resetSupplierForm();
  if (supplier) {
    editingSupplierId.value = supplier.id;
    Object.assign(supplierForm, {
      ...supplier,
      document: supplier.document || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      contact_name: supplier.contact_name || '',
      website: supplier.website || '',
      notes: supplier.notes || '',
    });
  }
  supplierDialogRef.value?.open();
};

const openMovementDialog = (product = null) => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }

  resetMovementForm(product);
  movementDialogRef.value?.open();
};

const closeProductDialog = () => productDialogRef.value?.close();
const closeSupplierDialog = () => supplierDialogRef.value?.close();
const closeMovementDialog = () => movementDialogRef.value?.close();

const saveProduct = async () => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }

  if (!productFormValid.value) {
    useAlert(copy.messages.productRequired);
    return;
  }

  isSaving.value = true;
  try {
    if (productForm.location) {
      stockLocations.value = [
        productForm.location,
        ...stockLocations.value.filter(item => item !== productForm.location),
      ];
      persistStockLocations();
    }

    const payload = {
      ...productForm,
      cost_price: Number(productForm.cost_price || 0),
      sale_price: Number(productForm.sale_price || 0),
    };
    if (editingProductId.value) {
      await TalkviaInventoryProductsAPI.update(editingProductId.value, payload);
    } else {
      await TalkviaInventoryProductsAPI.create(payload);
    }
    closeProductDialog();
    useAlert(copy.messages.saved);
    await loadInventory();
  } catch {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};

const saveSupplier = async () => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }

  if (!supplierFormValid.value) {
    useAlert(copy.messages.supplierRequired);
    return;
  }

  isSaving.value = true;
  try {
    const payload = { ...supplierForm };
    if (editingSupplierId.value) {
      await TalkviaInventorySuppliersAPI.update(
        editingSupplierId.value,
        payload
      );
    } else {
      await TalkviaInventorySuppliersAPI.create(payload);
    }
    closeSupplierDialog();
    useAlert(copy.messages.saved);
    await loadInventory();
  } catch {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};

const saveMovement = async () => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }

  if (
    !movementForm.talkvia_inventory_product_id ||
    Number(movementForm.quantity) <= 0
  ) {
    useAlert(copy.messages.movementRequired);
    return;
  }
  if (movementWouldBeNegative.value) {
    useAlert(copy.messages.negativeStock);
    return;
  }

  isSaving.value = true;
  try {
    await TalkviaInventoryMovementsAPI.create({
      ...movementForm,
      unit_cost: Number(movementForm.unit_cost || 0),
    });
    closeMovementDialog();
    useAlert(copy.messages.movementSaved);
    await loadInventory();
  } catch {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};

// ─── Group CRUD ───────────────────────────────────────────────────────────────

const resetGroupForm = () => {
  editingGroupId.value = null;
  Object.assign(groupForm, {
    name: '',
    description: '',
    sale_price: 0,
    active: true,
    items: [],
  });
};

const openGroupDialog = (group = null) => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }
  resetGroupForm();
  if (group) {
    editingGroupId.value = group.id;
    Object.assign(groupForm, {
      name: group.name,
      description: group.description || '',
      sale_price: Number(group.sale_price || 0),
      active: group.active,
      items: (group.items || []).map(item => ({ ...item })),
    });
  }
  groupDialogRef.value?.open();
};

const closeGroupDialog = () => groupDialogRef.value?.close();

const addGroupItem = () => {
  groupForm.items.push({
    product_id: '',
    quantity: 1,
    position: groupForm.items.length,
  });
};

const removeGroupItem = index => {
  groupForm.items.splice(index, 1);
};

const saveGroup = async () => {
  if (!groupForm.name.trim()) {
    useAlert('O nome do grupo é obrigatório.');
    return;
  }
  if (!groupForm.items.length || groupForm.items.some(i => !i.product_id)) {
    useAlert('Adicione ao menos um produto ao grupo.');
    return;
  }

  isSaving.value = true;
  try {
    const payload = { ...groupForm, items: groupForm.items };
    if (editingGroupId.value) {
      await TalkviaInventoryProductGroupsAPI.update(
        editingGroupId.value,
        payload
      );
    } else {
      await TalkviaInventoryProductGroupsAPI.create(payload);
    }
    closeGroupDialog();
    useAlert(copy.messages.saved);
    await loadInventory();
  } catch (error) {
    useAlert(error?.response?.data?.error || copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};

const deleteGroup = async group => {
  if (!canManageInventory.value) return;
  isSaving.value = true;
  try {
    await TalkviaInventoryProductGroupsAPI.delete(group.id);
    useAlert('Grupo excluído.');
    await loadInventory();
  } catch {
    useAlert(copy.messages.deleteFailed);
  } finally {
    isSaving.value = false;
  }
};

const groupActionItems = [
  { label: copy.actions.edit, value: 'edit', icon: 'i-lucide-pencil' },
  { label: copy.actions.delete, value: 'delete', icon: 'i-lucide-trash-2' },
];

const closeMenu = () => {
  activeMenuProduct.value = null;
  activeMenuGroup.value = null;
  menuAnchor.value = null;
};

const openGroupMenu = (group, event) => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }
  const rect = event.currentTarget.getBoundingClientRect();
  menuAnchor.value = {
    top: rect.bottom + 4,
    right: window.innerWidth - rect.right,
  };
  activeMenuGroup.value = group;
  activeMenuProduct.value = null;
};

const handleGroupAction = ({ value }, group) => {
  closeMenu();
  if (value === 'edit') openGroupDialog(group);
  else if (value === 'delete') deleteGroup(group);
};

// ─────────────────────────────────────────────────────────────────────────────

const confirmDeleteProduct = product => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }
  productToDelete.value = product;
  deleteDialogRef.value?.open();
};

const executeDeleteProduct = async () => {
  if (!productToDelete.value) return;
  isSaving.value = true;
  try {
    await TalkviaInventoryProductsAPI.delete(productToDelete.value.id);
    deleteDialogRef.value?.close();
    productToDelete.value = null;
    useAlert(copy.messages.deleted);
    await loadInventory();
  } catch (error) {
    const msg = error?.response?.data?.error || copy.messages.deleteFailed;
    deleteDialogRef.value?.close();
    useAlert(msg);
  } finally {
    isSaving.value = false;
  }
};

const productActionItems = [
  { label: copy.actions.edit, value: 'edit', icon: 'i-lucide-pencil' },
  {
    label: copy.actions.movement,
    value: 'movement',
    icon: 'i-lucide-arrow-right-left',
  },
  { label: copy.actions.delete, value: 'delete', icon: 'i-lucide-trash-2' },
];

const openProductMenu = (product, event) => {
  if (!canManageInventory.value) {
    useAlert(copy.messages.managePermissionRequired);
    return;
  }
  const rect = event.currentTarget.getBoundingClientRect();
  menuAnchor.value = {
    top: rect.bottom + 4,
    right: window.innerWidth - rect.right,
  };
  activeMenuProduct.value = product;
  activeMenuGroup.value = null;
};

const handleProductAction = ({ value }, product) => {
  closeMenu();
  if (value === 'edit') openProductDialog(product);
  else if (value === 'movement') openMovementDialog(product);
  else if (value === 'delete') confirmDeleteProduct(product);
};

const clearFilters = () => {
  Object.assign(filters, {
    q: '',
    supplier_id: '',
    category: '',
    active: '',
  });
  loadInventory();
};

const handleInventoryTabChange = tab => {
  activeInventoryTab.value = tab.key;
};

onMounted(() => {
  loadStockLocations();
  loadInventory();
});
</script>

<template>
  <main
    class="inventory-page mx-auto flex w-full max-w-7xl flex-col gap-6 overflow-y-auto px-6 py-8 pb-32"
  >
    <header class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p
          class="mb-1 text-xs font-semibold uppercase tracking-wide text-n-brand"
        >
          {{ copy.fields.brand }}
        </p>
        <h1 class="mb-2 text-3xl font-semibold text-n-slate-12">
          {{ copy.title }}
        </h1>
        <p class="mb-0 max-w-3xl text-base text-n-slate-11">
          {{ copy.subtitle }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <Button
          :label="copy.actions.newSupplier"
          color="slate"
          variant="faded"
          :disabled="!canManageInventory"
          @click="openSupplierDialog()"
        />
        <Button
          :label="copy.actions.movement"
          color="slate"
          variant="faded"
          :disabled="!canManageInventory"
          @click="openMovementDialog()"
        />
        <Button
          :label="copy.actions.newProduct"
          :disabled="!canManageInventory"
          @click="openProductDialog()"
        />
      </div>
    </header>

    <section class="grid gap-4 md:grid-cols-4">
      <article
        v-for="card in cards"
        :key="card.label"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-5"
        :class="
          card.label === copy.cards.lowStock && Number(card.value) > 0
            ? '!border-n-amber-7 bg-n-amber-2'
            : ''
        "
      >
        <div class="mb-4 flex items-center justify-between">
          <p class="mb-0 text-xs font-semibold uppercase text-n-slate-10">
            {{ card.label }}
          </p>
          <span
            class="size-5"
            :class="[
              card.icon,
              card.label === copy.cards.lowStock && Number(card.value) > 0
                ? 'text-n-amber-11'
                : 'text-n-brand',
            ]"
          />
        </div>
        <p class="mb-0 text-3xl font-semibold text-n-slate-12">
          {{ card.value }}
        </p>
      </article>
    </section>

    <section class="rounded-lg border border-n-weak bg-n-solid-1 p-5">
      <div class="inventory-filter-grid">
        <label class="inventory-filter-field">
          <span class="text-sm font-medium text-n-slate-11">
            {{ copy.fields.search }}
          </span>
          <Input
            v-model="filters.q"
            :custom-input-class="filterInputClass"
            :placeholder="copy.fields.searchPlaceholder"
            @enter="loadInventory"
          />
        </label>
        <div class="inventory-filter-field">
          <span class="text-sm font-medium text-n-slate-11">
            {{ copy.fields.supplier }}
          </span>
          <ComboBox
            v-model="filters.supplier_id"
            :options="supplierFilterOptions"
            :placeholder="copy.statuses.all"
            search-placeholder="Buscar fornecedor"
            teleport-to-body
          />
        </div>
        <div class="inventory-filter-field">
          <span class="text-sm font-medium text-n-slate-11">
            {{ copy.fields.category }}
          </span>
          <ComboBox
            v-model="filters.category"
            :options="categoryFilterOptions"
            :placeholder="copy.statuses.allCategories"
            search-placeholder="Buscar categoria"
            teleport-to-body
          />
        </div>
        <div class="inventory-filter-field">
          <span class="text-sm font-medium text-n-slate-11">
            {{ copy.fields.status }}
          </span>
          <ComboBox
            v-model="filters.active"
            :options="statusFilterOptions"
            :placeholder="copy.statuses.all"
            teleport-to-body
          />
        </div>
        <div class="inventory-filter-actions">
          <Button
            type="button"
            :label="
              activeFilterCount
                ? `${copy.actions.filter} (${activeFilterCount})`
                : copy.actions.filter
            "
            @click="loadInventory"
          />
          <Button
            type="button"
            :label="copy.actions.clear"
            color="slate"
            variant="ghost"
            @click="clearFilters"
          />
        </div>
      </div>
    </section>

    <section class="grid gap-6">
      <div class="rounded-lg border border-n-weak bg-n-solid-1">
        <div class="border-b border-n-weak p-5">
          <h2 class="mb-0 text-lg font-semibold text-n-slate-12">
            {{ copy.sections.products }}
          </h2>
        </div>
        <div v-if="isLoading" class="p-6 text-n-slate-11">
          {{ copy.labels.loading }}
        </div>
        <div
          v-else-if="!products.length"
          class="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center"
        >
          <span
            class="i-lucide-package-open size-9 rounded-xl bg-n-alpha-2 p-2 text-n-slate-10"
          />
          <div>
            <p class="mb-1 font-medium text-n-slate-12">
              {{ copy.empty.products }}
            </p>
            <p class="mb-0 text-sm text-n-slate-10">
              {{ copy.labels.productEmptyHint }}
            </p>
          </div>
          <Button
            v-if="canManageInventory && !activeFilterCount"
            :label="copy.actions.addProduct"
            icon="i-lucide-plus"
            size="sm"
            @click="openProductDialog()"
          />
        </div>
        <div v-else class="overflow-x-auto">
          <table class="w-full min-w-[980px] text-left">
            <thead class="border-b border-n-weak bg-n-alpha-2">
              <tr class="text-xs uppercase text-n-slate-10">
                <th class="px-5 py-3">{{ copy.fields.name }}</th>
                <th class="px-5 py-3">{{ copy.fields.supplier }}</th>
                <th class="px-5 py-3">{{ copy.fields.category }}</th>
                <th class="px-5 py-3">{{ copy.fields.quantity }}</th>
                <th class="px-5 py-3">{{ copy.fields.lowStock }}</th>
                <th class="px-5 py-3">{{ copy.fields.costPrice }}</th>
                <th class="px-5 py-3">{{ copy.fields.salePrice }}</th>
                <th class="px-5 py-3 text-right">
                  {{ copy.fields.actions }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="product in products" :key="product.id">
                <td class="px-5 py-4">
                  <div class="font-medium text-n-slate-12">
                    {{ product.name }}
                  </div>
                  <div class="text-sm text-n-slate-10">
                    {{ product.sku || '-' }} {{ copy.labels.separator }}
                    {{ product.location || '-' }}
                  </div>
                </td>
                <td class="px-5 py-4 text-n-slate-11">
                  {{ product.supplier?.name || copy.labels.noSupplier }}
                </td>
                <td class="px-5 py-4 text-n-slate-11">
                  {{ product.category || '-' }}
                </td>
                <td class="px-5 py-4 text-n-slate-12">
                  {{ formatQuantity(product.quantity_on_hand) }}
                </td>
                <td class="px-5 py-4 text-n-slate-11">
                  {{ formatQuantity(product.low_stock_threshold) }}
                </td>
                <td class="px-5 py-4 text-n-slate-11">
                  {{ formatMoney(product.cost_price) }}
                </td>
                <td class="px-5 py-4 text-n-slate-11">
                  {{ formatMoney(product.sale_price) }}
                </td>
                <td class="px-5 py-4">
                  <div class="flex justify-end">
                    <button
                      class="action-button action-button--menu"
                      :disabled="!canManageInventory"
                      @click="openProductMenu(product, $event)"
                    >
                      <span class="i-lucide-ellipsis-vertical size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Grupos de Produtos ─────────────────────────────────────────────── -->
      <div class="rounded-lg border border-n-weak bg-n-solid-1">
        <div
          class="flex items-center justify-between border-b border-n-weak px-5 py-4"
        >
          <h2 class="mb-0 text-lg font-semibold text-n-slate-12">
            {{ copy.sections.groups }}
          </h2>
          <Button
            v-if="canManageInventory"
            :label="copy.groupActions.newGroup"
            size="sm"
            icon="i-lucide-layers-2"
            @click="openGroupDialog()"
          />
        </div>
        <div v-if="!groups.length" class="p-6 text-n-slate-11">
          {{ copy.empty.groups }}
        </div>
        <div v-else class="overflow-x-auto">
          <table class="w-full min-w-[640px] text-left">
            <thead class="border-b border-n-weak bg-n-alpha-2">
              <tr class="text-xs uppercase text-n-slate-10">
                <th class="px-5 py-3">{{ copy.fields.name }}</th>
                <th class="px-5 py-3">{{ copy.fields.composition }}</th>
                <th class="px-5 py-3">{{ copy.fields.available }}</th>
                <th class="px-5 py-3">{{ copy.fields.salePrice }}</th>
                <th class="px-5 py-3 text-right">{{ copy.fields.actions }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="group in groups"
                :key="group.id"
                class="hover:bg-n-alpha-1"
              >
                <td class="px-5 py-4">
                  <p class="mb-0 font-medium text-n-slate-12">
                    {{ group.name }}
                  </p>
                  <p
                    v-if="group.description"
                    class="mb-0 text-xs text-n-slate-10"
                  >
                    {{ group.description }}
                  </p>
                </td>
                <td class="px-5 py-4">
                  <ul class="mb-0 space-y-0.5">
                    <li
                      v-for="item in group.items"
                      :key="item.id"
                      class="flex items-center gap-1 text-sm text-n-slate-11"
                    >
                      <span
                        class="i-lucide-package size-3.5 shrink-0 text-n-slate-10"
                      />
                      {{ formatQuantity(item.quantity)
                      }}{{ copy.labels.quantityMultiplier }}
                      {{ item.product_name }}
                    </li>
                  </ul>
                </td>
                <td class="px-5 py-4 text-n-slate-12">
                  {{ formatQuantity(group.quantity_on_hand) }}
                </td>
                <td class="px-5 py-4 text-n-slate-11">
                  {{ formatMoney(group.sale_price) }}
                </td>
                <td class="px-5 py-4">
                  <div class="flex justify-end">
                    <button
                      class="action-button action-button--menu"
                      :disabled="!canManageInventory"
                      @click="openGroupMenu(group, $event)"
                    >
                      <span class="i-lucide-ellipsis-vertical size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- ───────────────────────────────────────────────────────────────────── -->

      <section class="rounded-lg border border-n-weak bg-n-solid-1">
        <nav
          class="overflow-x-auto border-b border-n-weak p-3"
          :aria-label="copy.labels.inventoryInfo"
        >
          <TabBar
            :tabs="inventoryTabBarItems"
            :initial-active-tab="activeInventoryTabIndex"
            @tab-changed="handleInventoryTabChange"
          />
        </nav>

        <div class="p-5">
          <div v-if="activeInventoryTab === 'alerts'">
            <div
              v-if="!lowStockProducts.length"
              class="text-sm text-n-slate-11"
            >
              {{ copy.empty.alerts }}
            </div>
            <div v-else class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <article
                v-for="product in lowStockProducts"
                :key="product.id"
                class="rounded-lg border border-n-ruby-5 bg-n-ruby-2 p-3"
              >
                <p class="mb-1 font-medium text-n-ruby-12">
                  {{ product.name }}
                </p>
                <p class="mb-0 text-sm text-n-ruby-11">
                  {{ formatQuantity(product.quantity_on_hand) }}
                  {{ copy.labels.inStock }}
                  {{ copy.labels.minimum }}
                  {{ formatQuantity(product.low_stock_threshold)
                  }}{{ copy.labels.period }}
                </p>
              </article>
            </div>
          </div>

          <div v-else-if="activeInventoryTab === 'suppliers'">
            <div class="mb-4 flex justify-end">
              <button class="link-button" @click="openSupplierDialog()">
                {{ copy.actions.newSupplier }}
              </button>
            </div>
            <div v-if="!suppliers.length" class="text-sm text-n-slate-11">
              {{ copy.empty.suppliers }}
            </div>
            <div v-else class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              <button
                v-for="supplier in suppliers"
                :key="supplier.id"
                class="rounded-lg border border-n-weak p-3 text-left hover:bg-n-alpha-2"
                @click="openSupplierDialog(supplier)"
              >
                <p class="mb-1 font-medium text-n-slate-12">
                  {{ supplier.name }}
                </p>
                <p class="mb-0 text-sm text-n-slate-10">
                  {{
                    supplier.contact_name ||
                    supplier.email ||
                    supplier.phone ||
                    '-'
                  }}
                </p>
              </button>
            </div>
          </div>

          <div v-else>
            <div
              v-if="!movements.length"
              class="flex flex-col items-center gap-2 py-8 text-center"
            >
              <span class="i-lucide-history size-6 text-n-slate-10" />
              <p class="mb-0 text-sm text-n-slate-11">
                {{ copy.labels.noMovements }}
              </p>
              <Button
                v-if="canManageInventory && products.length"
                :label="copy.actions.movement"
                icon="i-lucide-arrow-right-left"
                color="slate"
                variant="faded"
                size="sm"
                @click="openMovementDialog()"
              />
            </div>
            <div v-else class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <article
                v-for="movement in movements.slice(0, 8)"
                :key="movement.id"
                class="rounded-lg bg-n-alpha-2 p-3"
              >
                <p class="mb-1 text-sm font-medium text-n-slate-12">
                  {{ movement.product_name }}
                </p>
                <p class="mb-1 text-sm text-n-slate-11">
                  {{ movementLabel(movement.movement_type) }}
                  {{ copy.labels.separator }}
                  {{ formatQuantity(movement.quantity) }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ formatDateTime(movement.created_at) }}
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>
    </section>

    <!-- ── Dialog: Grupo de Produtos ──────────────────────────────────────── -->
    <Dialog
      ref="groupDialogRef"
      width="3xl"
      position="top"
      overflow-y-auto
      sticky-header-footer
      :title="
        editingGroupId
          ? copy.groupActions.editGroup
          : copy.groupActions.newGroup
      "
      :description="copy.labels.groupDescription"
      :confirm-button-label="
        editingGroupId ? copy.actions.saveGroup : copy.actions.createGroup
      "
      :cancel-button-label="copy.actions.cancel"
      :is-loading="isSaving"
      :disable-confirm-button="!groupFormValid || !canManageInventory"
      @confirm="saveGroup"
    >
      <template #default>
        <div class="grid gap-5">
          <div class="grid gap-4 md:grid-cols-2">
            <label class="grid gap-1 md:col-span-2">
              <span class="field-label">{{ copy.fields.groupName }}</span>
              <input
                v-model="groupForm.name"
                class="form-input"
                :placeholder="copy.labels.groupNamePlaceholder"
              />
            </label>
            <label class="grid gap-1">
              <span class="field-label">{{ copy.fields.groupSalePrice }}</span>
              <input
                :value="groupSaleDisplay"
                class="form-input"
                inputmode="numeric"
                @input="groupSaleDisplay = $event.target.value"
              />
            </label>
            <label class="grid gap-1">
              <span class="field-label">{{ copy.fields.status }}</span>
              <div class="select-wrapper">
                <select
                  v-model="groupForm.active"
                  class="form-input select-input pr-8 cursor-pointer"
                >
                  <option :value="true">{{ copy.statuses.active }}</option>
                  <option :value="false">{{ copy.statuses.inactive }}</option>
                </select>
                <span class="i-lucide-chevron-down select-chevron" />
              </div>
            </label>
            <label class="grid gap-1 md:col-span-2">
              <span class="field-label">{{ copy.fields.description }}</span>
              <input
                v-model="groupForm.description"
                class="form-input"
                :placeholder="copy.labels.optionalDescription"
              />
            </label>
          </div>

          <div class="grid gap-3">
            <div class="flex items-center justify-between">
              <span class="field-label">{{
                copy.fields.groupComposition
              }}</span>
              <button
                type="button"
                class="btn btn-ghost px-2 py-1 text-xs text-n-brand"
                @click="addGroupItem"
              >
                + {{ copy.groupActions.addItem }}
              </button>
            </div>

            <p
              v-if="!groupForm.items.length"
              class="mb-0 text-sm text-n-slate-10"
            >
              {{ copy.labels.groupEmptyHint }}
            </p>

            <div
              v-for="(item, index) in groupForm.items"
              :key="index"
              class="group-item-row"
            >
              <label class="min-w-0 flex-1 grid gap-1">
                <span class="field-label">{{ copy.fields.product }}</span>
                <div class="select-wrapper">
                  <select
                    v-model="item.product_id"
                    class="form-input select-input pr-8 cursor-pointer"
                  >
                    <option value="">{{ copy.labels.selectProduct }}</option>
                    <option
                      v-for="product in products.filter(p => p.active)"
                      :key="product.id"
                      :value="product.id"
                    >
                      {{ product.name
                      }}{{ product.sku ? ` (#${product.sku})` : '' }}
                    </option>
                  </select>
                  <span class="i-lucide-chevron-down select-chevron" />
                </div>
              </label>
              <label class="w-24 shrink-0 grid gap-1">
                <span class="field-label">{{ copy.fields.shortQuantity }}</span>
                <input
                  v-model.number="item.quantity"
                  class="form-input quantity-input"
                  inputmode="numeric"
                  :placeholder="String(1)"
                />
              </label>
              <button
                type="button"
                class="btn btn-secondary shrink-0 self-end px-3"
                @click="removeGroupItem(index)"
              >
                <span class="i-lucide-trash-2 size-4 text-n-ruby-11" />
              </button>
            </div>
          </div>
        </div>
      </template>
    </Dialog>
    <!-- ───────────────────────────────────────────────────────────────────── -->

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="copy.messages.deleteTitle"
      :description="
        productToDelete ? copy.messages.confirmDelete(productToDelete.name) : ''
      "
      :confirm-button-label="copy.actions.delete"
      :cancel-button-label="copy.actions.cancel"
      :is-loading="isSaving"
      @confirm="executeDeleteProduct"
    />

    <Dialog
      ref="productDialogRef"
      width="3xl"
      position="top"
      overflow-y-auto
      sticky-header-footer
      :title="
        editingProductId ? copy.labels.editProduct : copy.actions.newProduct
      "
      :description="copy.labels.productDescription"
      :confirm-button-label="copy.actions.saveProduct"
      :cancel-button-label="copy.actions.cancel"
      :is-loading="isSaving"
      :disable-confirm-button="!productFormValid || !canManageInventory"
      @confirm="saveProduct"
    >
      <template #default>
        <div class="flex flex-col gap-5">
          <section class="inventory-form-section">
            <div class="inventory-form-section__title">
              <span class="i-lucide-package-search size-4" />
              {{ copy.labels.productIdentification }}
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
              <Input
                v-model="productForm.name"
                :label="`${copy.fields.name} *`"
                placeholder="Ex: Cadeira executiva"
                autofocus
              />
              <div class="inventory-native-field">
                <span class="inventory-native-field__label">
                  {{ copy.fields.supplier }}
                </span>
                <ComboBox
                  v-model="productForm.talkvia_inventory_supplier_id"
                  :options="supplierOptions"
                  :placeholder="copy.labels.noSupplier"
                  search-placeholder="Buscar fornecedor"
                  teleport-to-body
                />
              </div>
              <Input
                v-model="productForm.sku"
                :label="copy.fields.sku"
                placeholder="Ex: CAD-001"
              />
              <Input
                v-model="productForm.barcode"
                :label="copy.fields.barcode"
                placeholder="EAN, UPC ou código interno"
              />
              <Input
                v-model="productForm.category"
                :label="copy.fields.category"
                placeholder="Ex: Móveis"
              />
              <div class="inventory-native-field">
                <span class="inventory-native-field__label">
                  {{ copy.fields.unit }} *
                </span>
                <ComboBox
                  v-model="productForm.unit"
                  :options="unitOptions"
                  :placeholder="copy.labels.selectUnit"
                  teleport-to-body
                />
              </div>
            </div>
          </section>

          <section class="inventory-form-section">
            <div class="inventory-form-section__title">
              <span class="i-lucide-badge-dollar-sign size-4" />
              {{ copy.labels.productCommercial }}
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
              <Input
                v-model="productCostDisplay"
                :label="copy.fields.costPrice"
                inputmode="decimal"
              />
              <Input
                v-model="productSaleDisplay"
                :label="copy.fields.salePrice"
                inputmode="decimal"
              />
            </div>
            <div class="inventory-margin-summary">
              <span>{{ copy.labels.estimatedMargin }}</span>
              <strong
                :class="productMargin < 0 ? 'text-n-ruby-11' : 'text-n-teal-11'"
              >
                {{ formatMoney(productMargin) }} ·
                {{
                  productMarginPercent.toLocaleString('pt-BR', {
                    maximumFractionDigits: 1,
                  })
                }}%
              </strong>
            </div>
          </section>

          <section class="inventory-form-section">
            <div class="inventory-form-section__title">
              <span class="i-lucide-warehouse size-4" />
              {{ copy.labels.productInventory }}
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
              <Input
                v-model="productForm.quantity_on_hand"
                :label="copy.fields.quantity"
                type="number"
                min="0"
                step="0.001"
                :disabled="!!editingProductId"
                :message="
                  editingProductId
                    ? copy.labels.directQuantityWarning
                    : 'Saldo inicial do produto.'
                "
              />
              <Input
                v-model="productForm.low_stock_threshold"
                :label="copy.fields.lowStock"
                type="number"
                min="0"
                step="0.001"
                message="Você será alertado quando o saldo atingir este valor."
              />
              <div class="inventory-native-field sm:col-span-2">
                <span class="inventory-native-field__label">
                  {{ copy.fields.location }}
                </span>
                <ComboBox
                  v-model="productForm.location"
                  :options="locationSelectOptions"
                  :placeholder="copy.labels.selectLocation"
                  search-placeholder="Buscar local"
                  empty-state="Nenhum local cadastrado"
                  teleport-to-body
                />
              </div>
              <div class="sm:col-span-2">
                <Button
                  type="button"
                  :label="
                    showNewLocationField
                      ? copy.labels.hideNewLocation
                      : copy.labels.addNewLocation
                  "
                  :icon="showNewLocationField ? 'i-lucide-x' : 'i-lucide-plus'"
                  color="slate"
                  variant="ghost"
                  size="sm"
                  @click="showNewLocationField = !showNewLocationField"
                />
                <div
                  v-if="showNewLocationField"
                  class="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]"
                >
                  <Input
                    v-model="newStockLocation"
                    placeholder="Ex: Depósito A · Corredor 2"
                    @enter="addStockLocation"
                  />
                  <Button
                    type="button"
                    :label="copy.labels.addLocation"
                    color="slate"
                    variant="faded"
                    :disabled="!newStockLocation.trim()"
                    @click="addStockLocation"
                  />
                </div>
              </div>
            </div>
          </section>

          <section class="inventory-form-section">
            <div class="inventory-form-section__title">
              <span class="i-lucide-notebook-text size-4" />
              {{ copy.labels.productDetails }}
            </div>
            <TextArea
              v-model="productForm.description"
              :label="copy.fields.description"
              :placeholder="copy.labels.productDescriptionPlaceholder"
              :max-length="1000"
              show-character-count
              auto-height
              resize
            />
            <div class="grid gap-2 sm:grid-cols-2">
              <label class="inventory-checkbox-card">
                <Checkbox v-model="productForm.active" />
                <span>
                  <strong>{{ copy.labels.productActive }}</strong>
                  <small>{{ copy.labels.productActiveHint }}</small>
                </span>
              </label>
              <label class="inventory-checkbox-card">
                <Checkbox v-model="productForm.returnable" />
                <span>
                  <strong>{{ copy.fields.returnable }}</strong>
                  <small>{{ copy.labels.returnableHint }}</small>
                </span>
              </label>
            </div>
          </section>
        </div>
      </template>
    </Dialog>

    <Dialog
      ref="supplierDialogRef"
      width="2xl"
      position="top"
      overflow-y-auto
      sticky-header-footer
      :title="
        editingSupplierId ? copy.labels.editSupplier : copy.actions.newSupplier
      "
      :description="copy.labels.supplierDescription"
      :confirm-button-label="copy.actions.saveSupplier"
      :cancel-button-label="copy.actions.cancel"
      :is-loading="isSaving"
      :disable-confirm-button="!supplierFormValid || !canManageInventory"
      @confirm="saveSupplier"
    >
      <template #default>
        <div class="flex flex-col gap-5">
          <section class="inventory-form-section">
            <div class="inventory-form-section__title">
              <span class="i-lucide-building-2 size-4" />
              {{ copy.labels.supplierContact }}
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
              <Input
                v-model="supplierForm.name"
                :label="`${copy.fields.name} *`"
                placeholder="Razão social ou nome fantasia"
                autofocus
              />
              <Input
                v-model="supplierForm.document"
                :label="copy.fields.document"
                placeholder="CNPJ, CPF ou identificador"
              />
              <Input
                v-model="supplierForm.contact_name"
                :label="copy.fields.contactName"
                placeholder="Pessoa de contato"
              />
              <Input
                v-model="supplierForm.phone"
                :label="copy.fields.phone"
                placeholder="(00) 00000-0000"
                inputmode="tel"
              />
              <Input
                v-model="supplierForm.email"
                :label="copy.fields.email"
                placeholder="contato@empresa.com"
                type="email"
              />
              <Input
                v-model="supplierForm.website"
                :label="copy.fields.website"
                placeholder="https://empresa.com"
                type="url"
              />
            </div>
          </section>
          <section class="inventory-form-section">
            <div class="inventory-form-section__title">
              <span class="i-lucide-notebook-text size-4" />
              {{ copy.labels.supplierDetails }}
            </div>
            <TextArea
              v-model="supplierForm.notes"
              :label="copy.fields.notes"
              :placeholder="copy.labels.supplierNotesPlaceholder"
              :max-length="1000"
              show-character-count
              auto-height
              resize
            />
            <label class="inventory-checkbox-card">
              <Checkbox v-model="supplierForm.active" />
              <span>
                <strong>{{ copy.labels.supplierActive }}</strong>
                <small>{{ copy.labels.supplierActiveHint }}</small>
              </span>
            </label>
          </section>
        </div>
      </template>
    </Dialog>

    <Dialog
      ref="movementDialogRef"
      width="2xl"
      position="top"
      overflow-y-auto
      sticky-header-footer
      :title="copy.actions.movement"
      :description="copy.labels.movementDescription"
      :confirm-button-label="copy.actions.saveMovement"
      :cancel-button-label="copy.actions.cancel"
      :is-loading="isSaving"
      :disable-confirm-button="!movementFormValid || !canManageInventory"
      @confirm="saveMovement"
    >
      <template #default>
        <div class="flex flex-col gap-5">
          <section class="inventory-form-section">
            <div class="inventory-form-section__title">
              <span class="i-lucide-arrow-right-left size-4" />
              {{ copy.labels.movementData }}
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
              <div class="inventory-native-field sm:col-span-2">
                <span class="inventory-native-field__label">
                  {{ copy.fields.name }} *
                </span>
                <ComboBox
                  v-model="movementForm.talkvia_inventory_product_id"
                  :options="productSelectOptions"
                  :placeholder="copy.labels.selectProduct"
                  :search-placeholder="copy.labels.searchProduct"
                  :empty-state="copy.labels.noProductsRegistered"
                  teleport-to-body
                />
              </div>
              <div class="inventory-native-field">
                <span class="inventory-native-field__label">
                  {{ copy.fields.movementType }} *
                </span>
                <ComboBox
                  v-model="movementForm.movement_type"
                  :options="movementTypeOptions"
                  teleport-to-body
                />
              </div>
              <Input
                v-model="movementForm.quantity"
                :label="`${copy.fields.quantity} *`"
                type="number"
                min="0.001"
                step="0.001"
                :message="
                  movementForm.movement_type === 'adjustment'
                    ? copy.labels.adjustmentHint
                    : ''
                "
                :message-type="movementWouldBeNegative ? 'error' : 'info'"
              />
              <Input
                v-model="movementUnitCostDisplay"
                :label="copy.fields.unitCost"
                inputmode="decimal"
              />
              <Input
                v-model="movementForm.reference"
                :label="copy.fields.reference"
                :placeholder="copy.labels.movementReferencePlaceholder"
              />
            </div>
          </section>

          <section
            v-if="selectedMovementProduct"
            class="inventory-balance-preview"
          >
            <div class="inventory-form-section__title">
              <span class="i-lucide-scale size-4" />
              {{ copy.labels.movementBalance }}
            </div>
            <div class="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <div>
                <small>{{ copy.labels.currentBalance }}</small>
                <strong>{{
                  formatQuantity(selectedMovementProduct.quantity_on_hand)
                }}</strong>
              </div>
              <span class="i-lucide-arrow-right size-5 text-n-slate-10" />
              <div>
                <small>{{ copy.labels.projectedBalance }}</small>
                <strong
                  :class="
                    movementWouldBeNegative
                      ? 'text-n-ruby-11'
                      : 'text-n-teal-11'
                  "
                >
                  {{ formatQuantity(movementProjectedQuantity) }}
                </strong>
              </div>
            </div>
            <p
              v-if="movementWouldBeNegative"
              class="mb-0 text-xs text-n-ruby-11"
            >
              {{ copy.messages.negativeStock }}
            </p>
          </section>

          <TextArea
            v-model="movementForm.notes"
            :label="copy.fields.notes"
            :placeholder="copy.labels.movementNotesPlaceholder"
            :max-length="1000"
            show-character-count
            auto-height
            resize
          />
        </div>
      </template>
    </Dialog>

    <Teleport to="body">
      <template v-if="menuAnchor && (activeMenuProduct || activeMenuGroup)">
        <div class="fixed inset-0 z-40" @click="closeMenu" />
        <div
          class="fixed z-50"
          :style="{
            top: menuAnchor.top + 'px',
            right: menuAnchor.right + 'px',
          }"
        >
          <DropdownMenu
            v-if="activeMenuProduct"
            :menu-items="productActionItems"
            class="right-0 top-0 w-36"
            @action="handleProductAction($event, activeMenuProduct)"
          />
          <DropdownMenu
            v-if="activeMenuGroup"
            :menu-items="groupActionItems"
            class="right-0 top-0 w-32"
            @action="handleGroupAction($event, activeMenuGroup)"
          />
        </div>
      </template>
    </Teleport>
  </main>
</template>

<style scoped>
.inventory-page {
  height: 100dvh;
}

.inventory-filter-grid {
  @apply grid gap-4 lg:items-end;
}

@media (min-width: 1024px) {
  .inventory-filter-grid {
    grid-template-columns:
      minmax(14rem, 1.3fr) minmax(12rem, 1fr)
      minmax(12rem, 1fr) minmax(10rem, 0.8fr) auto;
  }
}

.inventory-filter-field {
  @apply grid min-w-0 gap-1;
}

.inventory-filter-control {
  height: 48px;
  line-height: 1.25rem;
}

.inventory-filter-button {
  @apply !h-12 w-full !justify-between !rounded-lg !border !border-n-weak !bg-n-solid-2 !px-4 !text-sm !text-n-slate-12 shadow-sm;
}

.inventory-filter-button.is-active {
  @apply !border-n-brand !bg-n-alpha-2 ring-1 ring-n-brand;
}

.inventory-filter-actions {
  @apply flex items-end gap-3 self-end;
}

.form-input {
  @apply min-h-10 w-full rounded-lg border border-n-slate-5 dark:border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none transition focus:border-n-brand;
}

.select-input {
  margin-bottom: 0;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: none;
}

.select-input::-ms-expand {
  display: none;
}

.field-label {
  @apply text-sm font-medium text-n-slate-11;
}

.inventory-form-section {
  @apply flex flex-col gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4;
}

.inventory-form-section__title {
  @apply flex items-center gap-2 text-sm font-semibold text-n-slate-12;
}

.inventory-native-field {
  @apply flex min-w-0 flex-col gap-1;
}

.inventory-native-field__label {
  @apply mb-0.5 text-sm font-medium text-n-slate-12;
}

.inventory-margin-summary {
  @apply flex items-center justify-between gap-3 rounded-lg bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11;
}

.inventory-checkbox-card {
  @apply flex cursor-pointer items-start gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 transition hover:bg-n-alpha-2;
}

.inventory-checkbox-card span {
  @apply flex min-w-0 flex-col gap-1;
}

.inventory-checkbox-card strong {
  @apply text-sm font-medium text-n-slate-12;
}

.inventory-checkbox-card small {
  @apply text-xs leading-4 text-n-slate-10;
}

.inventory-balance-preview {
  @apply flex flex-col gap-3 rounded-xl border border-n-teal-7 bg-n-teal-2 p-4;
}

.inventory-balance-preview small {
  @apply mb-1 block text-xs text-n-slate-10;
}

.inventory-balance-preview strong {
  @apply block text-lg font-semibold;
}

.btn {
  @apply inline-flex min-h-10 items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60;
}

.btn-primary {
  @apply bg-n-brand text-white hover:opacity-90;
}

.btn-secondary {
  @apply border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2;
}

.btn-ghost {
  @apply text-n-slate-11 hover:text-n-slate-12;
}

.inventory-tabs {
  @apply flex gap-2 overflow-x-auto border-b border-n-weak p-3;
}

.inventory-tab-button {
  @apply inline-flex h-10 shrink-0 items-center gap-2 rounded-lg px-4 text-sm font-semibold text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12;
}

.inventory-tab-button--active {
  @apply bg-n-brand text-white hover:bg-n-brand hover:text-white;
}

.action-button {
  @apply inline-flex h-9 items-center justify-center rounded-lg px-3 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60;
}

.action-button--edit {
  @apply border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2;
}

.action-button--movement {
  @apply bg-n-brand text-white shadow-sm hover:brightness-95;
}

.action-button--delete {
  @apply border border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-4;
}

.action-button--menu {
  @apply w-9 border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2;
}

.group-item-row {
  @apply flex flex-nowrap items-end gap-2;
}

.select-wrapper {
  @apply relative;
}

.select-chevron {
  @apply pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-n-slate-10;
}

.quantity-input {
  appearance: textfield;
}

.quantity-input::-webkit-inner-spin-button,
.quantity-input::-webkit-outer-spin-button {
  appearance: none;
  margin: 0;
}

.link-button {
  @apply text-sm font-medium text-n-brand hover:underline;
}
</style>
